import merge from "lodash.merge";
import dateFormat from "date-fns/format";
import dateParse from "date-fns/parse";

const STATUS = {
    LOG_STATUS_NOT_ASSIGNED: "149001",
    LOG_STATUS_IN_PROGRESS: "149002",
    LOG_STATUS_ON_ERROR: "149003",
    LOG_STATUS_FINISHED: "149004",
    LOG_STATUS_NOT_AVAILABLE: "149005",
    LOG_STATUS_INTERNAL_ERROR: "149006"
};

let STATUS_CLASS = {};

STATUS_CLASS[STATUS.LOG_STATUS_NOT_ASSIGNED] = "badge badge-info";
STATUS_CLASS[STATUS.LOG_STATUS_IN_PROGRESS] = "badge badge-info";
STATUS_CLASS[STATUS.LOG_STATUS_ON_ERROR] = "badge badge-warning";
STATUS_CLASS[STATUS.LOG_STATUS_FINISHED] = "badge badge-success";
STATUS_CLASS[STATUS.LOG_STATUS_NOT_AVAILABLE] = "badge badge-default";
STATUS_CLASS[STATUS.LOG_STATUS_INTERNAL_ERROR] = "badge badge-danger";

const state = {
    updateRequests: {},
    isLoading: false,
    data: {},
};

const getters = {
    hasData: (state) => (tag) => state.updateRequests.hasOwnProperty(tag) && state.updateRequests[tag].hasData === true,
    isFirstRun: (state) => (tag) => {
        if (state.updateRequests.hasOwnProperty(tag) === false) {
            return true;
        }

        if (state.updateRequests.hasOwnProperty(tag) && state.updateRequests[tag].lastRun === 0) {
            return true;
        }

        return false;
    },
    getSystemAlerts: (state) => {
        let data = [];

        if (state.data.hasOwnProperty("load_status")) {
            data = data.concat(state.data.load_status);
        }

        if (state.data.hasOwnProperty("server_status")) {
            data = data.concat(state.data.server_status);
        }

        if (state.data.hasOwnProperty("service_status")) {
            data = data.concat(state.data.service_status);
        }

        return data;
    }
};

const actions = {
    async getNotifications({ dispatch, commit, state }) {
        try {
            let currentRun = Date.now();
            let callRequests = [];
            // let's calculate what is the object we need to send to the main platform call
            for (let tag in state.updateRequests) {
                if (state.updateRequests.hasOwnProperty(tag) === false) {
                    continue;
                }

                if (
                    state.updateRequests[tag].lastRun !== 0 &&
                    currentRun - state.updateRequests[tag].lastRun < state.updateRequests[tag].interval
                ) {
                    continue;
                }

                callRequests.push({ tag });
            }

            if (callRequests.length <= 0) {
                return true;
            }

            commit("setIsLoading", true);
            const response = await this._vm.$axios.post(this._vm.$laroute.action("get_platform_updates"), {
                requests: callRequests
            });
            commit("setIsLoading", false);

            if (response.data.hasOwnProperty("call_status") === false || response.data["call_status"] !== "OK") {
                // let's discuss how we can properly handle the error here. One idea would be to keep an iterator and notify the user at some point of the issue
                return true;
            }

            if (response.data.hasOwnProperty("results") === false || response.data["results"].length <= 0) {
                // let's discuss how we can properly handle the error here. One idea would be to keep an iterator and notify the user at some point of the issue
                return true;
            }

            await Promise.all(
                response.data["results"].map((result) => dispatch(state.updateRequests[result.tag].callback, result))
            );

            return true;
        } catch (e) {
            throw e;
        }
    },
    async _handleTransactionStatus({ commit, state }, response) {
        commit("setLastRun", { tag: response.tag, lastRun: Date.now() });

        if (response.hasOwnProperty("result") === false || response.result.length <= 0) {
            // handle error ?
            // throw e
            return;
        }

        let data = response.result.pop();

        if (data.hasOwnProperty("tag") === false || data.tag !== "transactions") {
            // handle error ?
            // throw e
            return;
        }

        if (data.hasOwnProperty("transactions") === false || data.transactions.length <= 0) {
            // handle error ?
            // throw e
            return;
        }

        commit("setHasData", { tag: response.tag, hasData: true });

        if (data.hasOwnProperty("has_error") === true && data.has_error === 1) {
            let message = data.hasOwnProperty("error_msg") ? data.error_msg : "System Error";

            commit("setData", {
                tag: response.tag,
                data: {
                    call_status: "FAILED",
                    message: message
                }
            });

            return true;
        }

        let transactions_sort_by_date = {};
        for (let i = 0; i < data.transactions.length; i++) {
            let log_time = dateParse(data.transactions[i].log_time * 1000); // milliseconds on javascript
            let log_key = dateFormat(log_time, "YYYY-MM-DD");

            if (transactions_sort_by_date.hasOwnProperty(log_key) === false) {
                transactions_sort_by_date[log_key] = [];
            }

            if (data.transactions[i].hasOwnProperty("status_code") === false) {
                data.transactions[i].status_code = STATUS.LOG_STATUS_NOT_AVAILABLE;
            }

            data.transactions[i].log_time = dateFormat(log_time, "HH:mm");
            data.transactions[i].status_class = STATUS_CLASS[data.transactions[i].status_code];

            transactions_sort_by_date[log_key].push(data.transactions[i]);
        }

        commit("setData", { tag: response.tag, data: transactions_sort_by_date });
    },
    async _handleServerStatus({ commit, state }, response) {
        commit("setLastRun", { tag: response.tag, lastRun: Date.now() });
        if (response.hasOwnProperty("result") === false || response.result.length <= 0) {
            // handle error ?
            // throw e
            return;
        }

        let data = [];

        for (let entry of response.result) {
            // let's make sure we have all the required keys
            if (
                entry.hasOwnProperty("tag") === false ||
                entry.hasOwnProperty("short_notice") === false ||
                entry.hasOwnProperty("server_list") === false
            ) {
                continue;
            }

            if (entry["server_list"].length <= 0) {
                continue;
            }

            data.push(entry);
        }

        commit("setHasData", { tag: response.tag, hasData: true });
        commit("setData", { tag: response.tag, data: data });
    },
    async _handleLoadStatus({ commit, state }, response) {
        commit("setLastRun", { tag: response.tag, lastRun: Date.now() });

        if (response.hasOwnProperty("result") === false || response.result.length <= 0) {
            // handle error ?
            // throw e
            return;
        }

        if (response.hasOwnProperty("tag") === false || response.tag !== "load_status") {
            // handle error ?
            // throw e
            return;
        }

        let data = [];

        for (let entry of response.result) {
            // let's make sure we have all the required keys
            if (
                entry.hasOwnProperty("tag") === false ||
                entry.hasOwnProperty("short_notice") === false ||
                entry.hasOwnProperty("server_list") === false
            ) {
                continue;
            }

            if (entry["tag"] !== "load2_servers" && entry["tag"] !== "load10_servers") {
                continue;
            }

            if (entry["server_list"].length <= 0) {
                continue;
            }

            data.push(entry);
        }

        commit("setHasData", { tag: response.tag, hasData: true });
        commit("setData", { tag: response.tag, data: data });
    },
    async _handleServiceStatus({ commit, state }, response) {
        commit("setLastRun", { tag: response.tag, lastRun: Date.now() });

        if (response.hasOwnProperty("result") === false || response.result.length <= 0) {
            // handle error ?
            // throw e
            return;
        }

        if (response.hasOwnProperty("tag") === false || response.tag !== "service_status") {
            // handle error ?
            // throw e
            return;
        }

        let data = [];

        for (let entry of response.result) {
            // let's make sure we have all the required keys
            if (
                entry.hasOwnProperty("tag") === false ||
                entry.hasOwnProperty("short_notice") === false ||
                entry.hasOwnProperty("server_list") === false
            ) {
                continue;
            }

            if (entry["server_list"].length <= 0) {
                continue;
            }

            data.push(entry);
        }

        commit("setHasData", { tag: response.tag, hasData: true });
        commit("setData", { tag: response.tag, data: data });
    }
};

const mutations = {
    /**
     *
     * @param state
     * @param payload
     * @example
     * let example_request_data = {
     *   'tag' : 'example tag',
     *   'callback': functionName,
     *   'interval': 10, // interval is in seconds
     *   ]
     * };
     */
    addPlatformUpdateRequest(state, { tag = false, callback = false, interval = 10 * 1000 } = {}) {
        if (tag === false) {
            return false;
        }

        if (callback === false) {
            return false;
        }

        if (typeof actions[callback] !== "function") {
            return false;
        }

        this._vm.$set(state.updateRequests, tag, {
            callback: callback,
            interval: interval,
            lastRun: 0,
            hasData: false
        });
    },
    setLastRun(state, payload) {
        let localUpdateRequests = merge(state.updateRequests, {
            [payload.tag]: {
                lastRun: payload.lastRun
            }
        });

        state.updateRequests = Object.assign({}, state.updateRequests, localUpdateRequests);
    },
    setHasData(state, payload) {
        let localUpdateRequests = merge(state.updateRequests, {
            [payload.tag]: {
                hasData: payload.hasData
            }
        });

        state.updateRequests = Object.assign({}, state.updateRequests, localUpdateRequests);
    },
    setIsLoading(state, payload) {
        state.isLoading = payload;
    },
    setData(state, payload) {
        let localData = {
            [payload.tag]: payload.data
        };

        state.data = Object.assign({}, state.data, localData);
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};
